/** @jsxImportSource @emotion/react */
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  fetchBatchOptions,
  fetchPaymentMethodOptions,
  fetchStudentStatusOptions,
} from 'helpers';

import Sidebar from 'components/layout/Sidebar/Sidebar';
import {
  FilterSection, FilterField, FilterActions, countActiveFilters,
} from 'components/layout/Sidebar/Filter';
import {
  CustomDatepicker as Datepicker,
  Select2,
  Select,
  CreatableSelect,
} from 'components/common';
import {
  filterConditionsContainerStyles,
  datepickerContainerStyles,
  select2ContainerStyles,
  selectContainerStyles,
} from './Sidebar.styles';

const FTPSidebar = ({
  startDate,
  endDate,
  setDateRange,
  filterCondition,
  setFilterCondition,
  handleChange,
  handleSelect2Change,
  handleSubmit,
  handleReset,
}) => {
  const [options, setOptions] = useState({
    batchOptions: [],
    studentStatusOptions: [],
    paymentMethodOptions: [],
  });

  const getBatchOptions = () => fetchBatchOptions(true).then((resp) => resp);

  const getStudentStatusOptions = () => fetchStudentStatusOptions().then((resp) => resp);
  const getPaymentMethodOptions = () => fetchPaymentMethodOptions().then((resp) => resp);

  const onFormSubmit = (e) => {
    e.preventDefault();
    handleSubmit(e);
  };

  useEffect(async () => {
    const [
      batchOptions,
      studentStatusOptions,
      paymentMethodOptions,
    ] = await Promise.all([
      getBatchOptions(),
      getStudentStatusOptions(),
      getPaymentMethodOptions(),
    ]);
    setOptions((prevState) => ({
      ...prevState,
      batchOptions,
      studentStatusOptions,
      paymentMethodOptions,
    }));
  }, []);

  return (
    <Sidebar
      search={{
        name: 'search',
        value: filterCondition.search,
        onChange: handleChange,
        onSubmit: onFormSubmit,
        placeholder: 'search by name / email',
      }}
      activeCount={countActiveFilters(filterCondition, startDate)}
    >
      <form onSubmit={onFormSubmit}>
        <div css={filterConditionsContainerStyles}>
          <FilterSection title="Dates">
            <FilterField label="date range">
              <Datepicker
                portalId="filter-panel-popover"
                containerStyles={datepickerContainerStyles}
                startDate={startDate}
                endDate={endDate}
                setDateRange={setDateRange}
                placeholder="date range"
              />
            </FilterField>
          </FilterSection>
          <FilterSection title="Batch">
            <FilterField label="registered batch">
              <Select2
                portalMenu
                containerStyles={select2ContainerStyles}
                defaultValue={{ label: 'registered batch', value: '' }}
                options={options.batchOptions}
                placeholder="registered batch"
                onChange={(e) => handleSelect2Change(e, 'appliedBatch')}
                value={filterCondition.appliedBatch}
              />
            </FilterField>
            <FilterField label="enrolled batch">
              <Select2
                portalMenu
                containerStyles={select2ContainerStyles}
                defaultValue={{ label: 'enrolled batch', value: '' }}
                options={options.batchOptions}
                placeholder="enrolled batch"
                onChange={(e) => handleSelect2Change(e, 'enrolledBatch')}
                value={filterCondition.enrolledBatch}
              />
            </FilterField>
          </FilterSection>
          <FilterSection title="Status">
            <FilterField label="payment type">
              <CreatableSelect
                portalMenu
                containerStyles={select2ContainerStyles}
                options={options.paymentMethodOptions}
                placeholder="payment type"
                value={filterCondition.paymentType}
                filterCondition={filterCondition}
                setFilterCondition={setFilterCondition}
                name="paymentType"
              />
            </FilterField>
            <FilterField label="student status">
              <Select
                containerStyles={selectContainerStyles}
                name="studentStatus"
                options={options.studentStatusOptions}
                placeholder="student status"
                value={filterCondition.studentStatus}
                onChange={handleChange}
              />
            </FilterField>
          </FilterSection>
        </div>
        <FilterActions applyType="submit" onReset={handleReset} />
      </form>
    </Sidebar>
  );
};

FTPSidebar.propTypes = {
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  setDateRange: PropTypes.func.isRequired,
  filterCondition: PropTypes.shape({
    appliedBatch: PropTypes.oneOfType([PropTypes.string, PropTypes.shape()]),
    enrolledBatch: PropTypes.oneOfType([PropTypes.string, PropTypes.shape()]),
    application_result: PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
    test_result: PropTypes.string,
    appliedProgramCode: PropTypes.string,
    paymentType: PropTypes.arrayOf(PropTypes.shape({})),
    paymentStatus: PropTypes.string,
    studentStatus: PropTypes.string,
    search: PropTypes.string,
  }).isRequired,
  setFilterCondition: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleSelect2Change: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleReset: PropTypes.func.isRequired,
  // handleDownloadCSV: PropTypes.func.isRequired,
  // isCsvDownloading: PropTypes.bool.isRequired,
};

FTPSidebar.defaultProps = {
  startDate: null,
  endDate: null,
};

export default FTPSidebar;
